import { useCallback, useMemo, useState } from 'react';
import type { TiptapDoc } from '@odo/shared-ui';
import { useInvalidatePatientVirtualAppointments } from '../../../../hooks/useVirtualAppointments';
import {
    MAX_PLANNED_VIRTUAL_APPOINTMENTS,
    type VirtualAppointmentDto,
    type VirtualAppointmentPlan
} from '../../../../services/virtualAppointments';
import {
    createEmptyDraft,
    isDescriptionOverCap,
    parseSeenIds,
    parseVirtualAppointmentDraftPlans,
    triggerContextFor,
    validateDraft,
    type DraftTriggerContext,
    type VirtualAppointmentDraft,
    type VirtualAppointmentDraftErrors,
    type VirtualAppointmentDraftPatch
} from '../../../virtual-appointments/virtualAppointmentDraft';
import { extractVcaNoteFromEvent } from './clinicalEventFields';
import {
    plansSignature,
    resolveDraftFields,
    resolveSubmitPlans,
    vanishedDraftHint,
    type DraftPlanFields,
    type HydratedPlans,
    type LastValidDocs,
    type ResumedDraftPlans,
    type SavedNogoCounts,
    type VirtualAppointmentLoadStatus
} from './virtualAppointmentPlanMerge';
import { useVirtualAppointmentHydration } from './useVirtualAppointmentHydration';
import {
    offersWeeksFromNow,
    plannerTriggerProgress,
    type PlannedTriggerSeries,
    type PlannerTriggerScope
} from './plannerTriggerScope';
import { usePlannerTriggerScope } from './usePlannerTriggerScope';
import type { SavedTriggers } from '../../../virtual-appointments/virtualAppointmentTriggerAhead';
import type { TimelineEvent } from '../types';

// Spread into draft bodies. `virtualAppointments` absent = leave VAs untouched. No vcaNote is
// ever sent: a legacy note is converted into a VA instead.
export type ClinicalVirtualAppointmentDraftFields = DraftPlanFields;

export interface ClinicalVirtualAppointmentSubmitFields {
    virtualAppointments: VirtualAppointmentPlan[];
}

export interface VirtualAppointmentPlansState {
    loadStatus: VirtualAppointmentLoadStatus;
    retryLoad: () => void;
    planned: VirtualAppointmentDraft[];
    frozen: VirtualAppointmentDto[];
    removedElsewhere: VirtualAppointmentDraft[];
    /** The patient's progress the editor shows and checks against; null keeps the context-free checks. */
    triggerScope: PlannerTriggerScope | null;
    /** Whether a week may count from now; never on a refinement's next series. */
    offerWeeksFromNow: boolean;
    /** The saved planned VAs' triggers, by id. */
    savedTriggers: SavedTriggers;
    /** Saved planned no-go VAs' running counts, by id. */
    savedNogoCounts: SavedNogoCounts;
    errorsByKey: Record<string, VirtualAppointmentDraftErrors>;
    /** The VA created from a legacy note, if any; selected on load. */
    convertedLegacyKey: string | null;
    /** True while that converted VA exists and still lacks its type or trigger. */
    legacyConversionPending: boolean;
    canAdd: boolean;
    /** Returns the new row's key, or null when nothing was added. */
    add: () => string | null;
    update: (key: string, patch: VirtualAppointmentDraftPatch) => void;
    remove: (key: string) => void;
    isDirty: boolean;
    submitBlockedHint: string | undefined;
    /** The submit block given the wizard's current draft id; covers a draft deleted elsewhere. */
    submitBlockFor: (draftId: string | null) => string | undefined;
    draftFields: ClinicalVirtualAppointmentDraftFields;
    /** Null while submit must stay blocked (loading, load failure, invalid rows). */
    submitFields: ClinicalVirtualAppointmentSubmitFields | null;
    onSubmitted: () => void;
}

interface UseVirtualAppointmentPlansParams {
    patientId: string;
    editingEvent?: TimelineEvent;
    resumingDraft?: TimelineEvent;
    series: PlannedTriggerSeries;
}

const NO_PLANS: VirtualAppointmentDraft[] = [];
const NO_DTOS: VirtualAppointmentDto[] = [];

// Each row's latest description under the node cap, used when a draft save cannot take the current one.
function useLastValidDocs(hydratedPlanned: VirtualAppointmentDraft[] | undefined) {
    const [recorded, setRecorded] = useState<ReadonlyMap<string, TiptapDoc>>(new Map());
    const lastValidDocs = useMemo<LastValidDocs>(() => {
        const loaded = (hydratedPlanned ?? NO_PLANS)
            .filter((d) => !isDescriptionOverCap(d.descriptionDoc))
            .map((d) => [d.key, d.descriptionDoc] as const);
        return new Map([...loaded, ...recorded]);
    }, [hydratedPlanned, recorded]);
    const record = useCallback((key: string, doc: TiptapDoc) => {
        if (isDescriptionOverCap(doc)) return;
        setRecorded((prev) => new Map(prev).set(key, doc));
    }, []);
    return { lastValidDocs, record };
}

function useEditablePlans(hydratedPlanned: VirtualAppointmentDraft[] | undefined) {
    const [edits, setEdits] = useState<VirtualAppointmentDraft[] | null>(null);
    const planned = edits ?? hydratedPlanned ?? NO_PLANS;
    const { lastValidDocs, record } = useLastValidDocs(hydratedPlanned);

    const edit = useCallback(
        (fn: (prev: VirtualAppointmentDraft[]) => VirtualAppointmentDraft[]) => {
            if (hydratedPlanned === undefined) return;
            setEdits((prev) => fn(prev ?? hydratedPlanned));
        },
        [hydratedPlanned]
    );
    const add = useCallback((): string | null => {
        if (hydratedPlanned === undefined || planned.length >= MAX_PLANNED_VIRTUAL_APPOINTMENTS) {
            return null;
        }
        const draft = createEmptyDraft();
        edit((prev) => [...prev, draft]);
        return draft.key;
    }, [edit, hydratedPlanned, planned.length]);
    const update = useCallback(
        (key: string, patch: VirtualAppointmentDraftPatch) => {
            if (patch.descriptionDoc) record(key, patch.descriptionDoc);
            edit((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
        },
        [edit, record]
    );
    const remove = useCallback(
        (key: string) => edit((prev) => prev.filter((d) => d.key !== key)),
        [edit]
    );
    return { planned, lastValidDocs, add, update, remove };
}

interface DerivedPlanOutputsInput {
    hydrated: HydratedPlans | null;
    planned: VirtualAppointmentDraft[];
    lastValidDocs: LastValidDocs;
    loadStatus: VirtualAppointmentLoadStatus;
    resumed: ResumedDraftPlans;
    legacyNote: string;
    isEditingDocument: boolean;
    triggerContext: DraftTriggerContext | undefined;
}

function useDerivedPlanOutputs({
    hydrated,
    planned,
    lastValidDocs,
    loadStatus,
    resumed,
    legacyNote,
    isEditingDocument,
    triggerContext
}: DerivedPlanOutputsInput) {
    const errorsByKey = useMemo<Record<string, VirtualAppointmentDraftErrors>>(
        () => Object.fromEntries(planned.map((d) => [d.key, validateDraft(d, triggerContext)])),
        [planned, triggerContext]
    );
    const baselineSignature = useMemo(
        () => (hydrated ? plansSignature(hydrated.planned) : null),
        [hydrated]
    );
    const submitResult = useMemo(
        () => resolveSubmitPlans(loadStatus, planned, errorsByKey),
        [loadStatus, planned, errorsByKey]
    );
    const draftFields = useMemo<ClinicalVirtualAppointmentDraftFields>(
        () => resolveDraftFields(hydrated, planned, resumed, lastValidDocs, legacyNote),
        [hydrated, planned, resumed, lastValidDocs, legacyNote]
    );
    const submitFields = useMemo<ClinicalVirtualAppointmentSubmitFields | null>(
        () => (submitResult.ok ? { virtualAppointments: submitResult.plans } : null),
        [submitResult]
    );
    const submitBlockedHint = submitResult.ok ? undefined : submitResult.blockedHint;
    const submitBlockFor = useCallback(
        (draftId: string | null) =>
            submitBlockedHint ?? vanishedDraftHint({ planned, isEditingDocument, draftId }),
        [submitBlockedHint, planned, isEditingDocument]
    );
    return {
        errorsByKey,
        isDirty: baselineSignature !== null && plansSignature(planned) !== baselineSignature,
        submitBlockedHint,
        submitBlockFor,
        draftFields,
        submitFields
    };
}

function legacyConversionState(
    hydrated: HydratedPlans | null,
    errorsByKey: Record<string, VirtualAppointmentDraftErrors>
): Pick<VirtualAppointmentPlansState, 'convertedLegacyKey' | 'legacyConversionPending'> {
    const convertedLegacyKey = hydrated?.convertedLegacyKey ?? null;
    const errors = convertedLegacyKey ? errorsByKey[convertedLegacyKey] : undefined;
    return {
        convertedLegacyKey,
        legacyConversionPending: Boolean(errors?.type ?? errors?.trigger)
    };
}

function triggerContextOf(
    scope: PlannerTriggerScope | null,
    savedTriggers: SavedTriggers,
    weeksFromNowAllowed: boolean
): DraftTriggerContext | undefined {
    if (scope) {
        return triggerContextFor(scope.treatmentStartDate, scope.dmLinked, {
            ...plannerTriggerProgress(scope),
            weeksFromNowAllowed,
            savedTriggers
        });
    }
    return weeksFromNowAllowed
        ? undefined
        : triggerContextFor(null, true, { weeksFromNowAllowed, savedTriggers });
}

function useTriggerContext(
    scope: PlannerTriggerScope | null,
    savedTriggers: SavedTriggers,
    weeksFromNowAllowed: boolean
): DraftTriggerContext | undefined {
    return useMemo(
        () => triggerContextOf(scope, savedTriggers, weeksFromNowAllowed),
        [scope, savedTriggers, weeksFromNowAllowed]
    );
}

export function useVirtualAppointmentPlans({
    patientId,
    editingEvent,
    resumingDraft,
    series
}: UseVirtualAppointmentPlansParams): VirtualAppointmentPlansState {
    const source = resumingDraft ?? editingEvent;
    const legacyNote = useMemo(() => (source ? extractVcaNoteFromEvent(source) : ''), [source]);
    const resumed = useMemo<ResumedDraftPlans>(
        () => ({
            plans: parseVirtualAppointmentDraftPlans(resumingDraft?.payload.virtualAppointments),
            seenIds: parseSeenIds(resumingDraft?.payload.virtualAppointmentsSeenIds)
        }),
        [resumingDraft]
    );
    const { hydrated, loadStatus, retry, nogoCounts, savedTriggers } =
        useVirtualAppointmentHydration({
            patientId,
            groupId: editingEvent?.groupId ?? resumingDraft?.groupId,
            draft: resumed,
            legacyNote
        });
    const triggerScope = usePlannerTriggerScope(patientId, series);
    const offerWeeksFromNow = offersWeeksFromNow(series);
    const triggerContext = useTriggerContext(triggerScope, savedTriggers, offerWeeksFromNow);
    const { planned, lastValidDocs, add, update, remove } = useEditablePlans(hydrated?.planned);
    const onSubmitted = useInvalidatePatientVirtualAppointments(patientId);
    const derived = useDerivedPlanOutputs({
        hydrated,
        planned,
        lastValidDocs,
        loadStatus,
        resumed,
        legacyNote,
        isEditingDocument: editingEvent !== undefined,
        triggerContext
    });
    const conversion = legacyConversionState(hydrated, derived.errorsByKey);

    return {
        ...derived,
        loadStatus,
        retryLoad: retry,
        planned,
        frozen: hydrated?.frozen ?? NO_DTOS,
        removedElsewhere: hydrated?.removedElsewhere ?? NO_PLANS,
        triggerScope,
        offerWeeksFromNow,
        savedTriggers,
        savedNogoCounts: nogoCounts,
        ...conversion,
        canAdd: loadStatus === 'ready' && planned.length < MAX_PLANNED_VIRTUAL_APPOINTMENTS,
        add,
        update,
        remove,
        onSubmitted
    };
}
